/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React , { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, BarChart3, Calculator, User, Users, BookOpen, Image, UserPlus } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';

// Reusing admin components with teacher context
import TeacherDashboard from './TeacherDashboard';
import ClassEditor from '../admin/ClassEditor';
import TestManagement from '../admin/TestManager';
import StudentProgress from '../admin/StudentProgress';
import TeacherImageGallery from './ImageGallery';
import GradebookView from './GradebookView';
import EnrollmentRequests from './EnrollmentRequests';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Stats', icon: BarChart3 },
  { key: 'gradebook', label: 'Gradebook', icon: Calculator },
  { key: 'enrollment', label: 'Requests', icon: UserPlus },
  { key: 'students', label: 'Students', icon: User },
  { key: 'classes', label: 'Classes', icon: Users },
  { key: 'tests', label: 'Tests', icon: BookOpen },
  { key: 'images', label: 'Images', icon: Image }
];

const TeacherPanel = ({ user }) => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  // Fetch pending enrollment request count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await fetch('/api/enrollment-requests/teacher/pending', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setPendingRequestCount(data.length);
        }
      } catch (error) {
        console.error('Error fetching pending request count:', error);
      }
    };

    fetchPendingCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const renderContent = () => {
    switch(activeSection) {
      case 'dashboard':
        return <TeacherDashboard user={user} />;
      case 'gradebook':
        return <GradebookView user={user} />;
      case 'enrollment':
        return <EnrollmentRequests />;
      case 'classes':
        return <ClassEditor teacherContext={true} teacherId={user.id} />;
      case 'tests':
        return <TestManagement teacherContext={true} teacherId={user.id} />;
      case 'students':
        return <StudentProgress teacherContext={true} teacherId={user.id} />;
      case 'images':
        return <TeacherImageGallery />;
      default:
        return <TeacherDashboard user={user} />;
    }
  };


  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="h-full w-full flex flex-col lg:flex-row overflow-hidden">
        {/* Navigation Sidebar - Mobile: Full width top bar, Desktop: Left sidebar */}
        <div className={`flex-shrink-0 ${sidebarCollapsed ? 'lg:w-16' : 'w-full lg:w-64'} border-b lg:border-r lg:border-b-0 bg-white shadow-sm transition-all duration-300`}>
          <nav className={`lg:space-y-1 py-4 ${sidebarCollapsed ? 'lg:px-2' : 'px-2 lg:px-4'}`}>
            <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1 lg:gap-1">
              {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
                const showBadge = key === 'enrollment' && pendingRequestCount > 0;

                const button = (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className={`flex items-center gap-3 text-sm font-medium whitespace-nowrap transition-all duration-200 rounded-lg relative
                      ${sidebarCollapsed
                        ? 'lg:justify-center lg:p-2.5 lg:w-full justify-center px-4 py-3 min-w-[80px]'
                        : 'justify-center lg:justify-start px-4 py-3 lg:w-full min-w-[80px] lg:min-w-0'
                      }
                      ${activeSection === key
                        ? 'bg-blue-600 text-white shadow-md border-b-2 border-blue-800 lg:border-b-0 lg:border-l-4 lg:border-l-blue-800'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 border-b-2 border-transparent lg:border-b-0 lg:border-l-4 lg:border-l-transparent'
                    }`}
                  >
                    <Icon className={`flex-shrink-0 ${sidebarCollapsed ? 'lg:w-5 lg:h-5 w-5 h-5' : 'w-5 h-5'}`} />
                    <span className={`font-semibold ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                      {label}
                      {showBadge && !sidebarCollapsed && (
                        <Badge
                          variant="destructive"
                          className="ml-2 px-1.5 py-0.5 text-xs"
                        >
                          {pendingRequestCount}
                        </Badge>
                      )}
                    </span>
                    {showBadge && sidebarCollapsed && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs rounded-full"
                      >
                        {pendingRequestCount}
                      </Badge>
                    )}
                  </button>
                );

                // Wrap with tooltip on desktop when collapsed
                if (sidebarCollapsed) {
                  return (
                    <React.Fragment key={key}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild className="hidden lg:block">
                          {button}
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            className="bg-gray-900 text-white px-3 py-2 rounded-md text-sm shadow-lg z-50"
                            sideOffset={5}
                          >
                            {label}
                            <Tooltip.Arrow className="fill-gray-900" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                      {/* Mobile version without tooltip */}
                      <div className="lg:hidden">
                        {button}
                      </div>
                    </React.Fragment>
                  );
                }

                return button;
              })}
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 bg-gray-50 w-full overflow-hidden relative flex flex-col">
          {/* Toggle Button - Desktop only */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`hidden lg:flex absolute top-4 z-30 items-center justify-center w-8 h-8 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition-all duration-300 ${
              sidebarCollapsed ? 'left-2' : 'left-4'
            }`}
            title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {activeSection === 'gradebook' ? (
            // Special layout for gradebook - no padding, fills entire height
            <div className="flex-1 flex flex-col overflow-hidden">
              {renderContent()}
            </div>
          ) : (
            // Standard layout for other sections with padding
            <div className="p-4 lg:p-6 max-w-full overflow-auto">
              {/* Header with Title */}
              <div className="mb-6">
                <h2 className="text-xl lg:text-2xl font-semibold lg:ml-10">
                  {NAV_ITEMS.find(item => item.key === activeSection)?.label || activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
                </h2>
              </div>

              {/* Dynamic Content Area */}
              <div className="mt-6 w-full">
                {renderContent()}
              </div>
            </div>
          )}
        </div>
      </div>
    </Tooltip.Provider>
  );
};

export default TeacherPanel;